import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HiXMark } from 'react-icons/hi2';
import { useGuideStore } from '../../stores/guideStore';
import { getPageNameKeyForRoute } from './guideContent';
import { GuideTOC } from './GuideTOC';
import { GuideArticle } from './GuideArticle';
import { GuideSearchResults } from './GuideSearchResults';
import styles from './GuidePanel.module.css';

export function GuidePanel() {
  const { t } = useTranslation('guide');
  const isOpen = useGuideStore((s) => s.isOpen);
  const activeArticleId = useGuideStore((s) => s.activeArticleId);
  const searchQuery = useGuideStore((s) => s.searchQuery);
  const closeGuide = useGuideStore((s) => s.closeGuide);
  const toggleGuide = useGuideStore((s) => s.toggleGuide);
  const setSearchQuery = useGuideStore((s) => s.setSearchQuery);
  const { pathname } = useLocation();

  // Keyboard shortcut: Ctrl+/ or Cmd+/
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        toggleGuide();
      }
      if (e.key === 'Escape' && isOpen) {
        closeGuide();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleGuide, closeGuide, isOpen]);

  if (!isOpen) return null;

  const pageNameKey = getPageNameKeyForRoute(pathname);
  const showSearch = searchQuery.trim().length >= 2;
  const isMac = navigator.platform.toUpperCase().includes('MAC');

  return (
    <>
      <div className={styles.overlay} onClick={closeGuide} />
      <aside className={styles.panel}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.headerTitle}>{t('title', 'User Guide')}</h2>
          <button className={styles.closeBtn} onClick={closeGuide} aria-label="Close">
            <HiXMark />
          </button>
        </div>

        {/* Search */}
        <div className={styles.searchBox}>
          <input
            className={styles.searchInput}
            type="text"
            placeholder={t('searchPlaceholder', 'Search guide...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
          {pageNameKey && !showSearch && !activeArticleId && (
            <div className={styles.contextLabel}>
              {t('helpFor', 'Help for')}: {t(pageNameKey)}
            </div>
          )}
        </div>

        {/* Content */}
        <div className={styles.content}>
          {showSearch ? (
            <GuideSearchResults />
          ) : activeArticleId ? (
            <GuideArticle />
          ) : (
            <GuideTOC />
          )}
        </div>

        {/* Footer shortcut hint */}
        <div className={styles.shortcut}>
          <kbd>{isMac ? '⌘' : 'Ctrl'}</kbd> + <kbd>/</kbd> {t('toToggle', 'to toggle')}
        </div>
      </aside>
    </>
  );
}
